import { Helmet } from "react-helmet-async";
import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Lightbox } from "../components/Lightbox";
import { useGaleria } from "../hooks/useCms";
import { mediaUrl } from "../lib/strapi";
import { NotFoundPage } from "./NotFoundPage";

/**
 * Detalle de una galería: /galeria/:slug
 *
 * Renderiza:
 *  - Cabecera con título, fecha y descripción
 *  - Enlace contextual a la edición o evento del que procede (si los hay)
 *  - Mosaico de thumbnails que abren el Lightbox al hacer clic
 *
 * Cuando llegan menos de 4 fotos, las pintamos a una columna mayor.
 * Las imágenes usan la variante "medium" de Strapi cuando existe;
 * si no, caen al original. La imagen ampliada del lightbox usa siempre
 * el original para mantener la calidad.
 */
export function GaleriaDetallePage() {
  const { slug } = useParams<{ slug: string }>();
  const { data: galeria, isLoading } = useGaleria(slug ?? "");
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  if (!slug) return <NotFoundPage />;

  if (isLoading) {
    return (
      <section className="container-emjc py-20">
        <p className="text-gray-700">Cargando galería…</p>
      </section>
    );
  }

  if (!galeria) return <NotFoundPage />;

  const fotos = galeria.fotos ?? [];
  const fechaTxt = galeria.fecha
    ? new Date(galeria.fecha).toLocaleDateString("es-ES", {
        day: "numeric",
        month: "long",
        year: "numeric",
      })
    : null;

  // Construimos el enlace contextual: si la galería pertenece a una edición
  // de programa, llevamos a la página de esa edición; si pertenece a un
  // evento, a la página del evento.
  const contextoLink =
    galeria.edicion && galeria.edicion.programa
      ? {
          to: `/programas/${galeria.edicion.programa.slug}/${galeria.edicion.slug}`,
          label: `${galeria.edicion.programa.nombre} ${galeria.edicion.anio}`,
        }
      : galeria.evento
        ? {
            to: `/eventos/${galeria.evento.slug}`,
            label: galeria.evento.titulo,
          }
        : null;

  return (
    <>
      <Helmet>
        <title>{galeria.titulo} — Galería — Campus EMJC</title>
        <meta
          name="description"
          content={galeria.descripcion ?? `Fotografías de ${galeria.titulo} — Campus EMJC.`}
        />
      </Helmet>

      <article className="container-emjc py-20">
        <nav aria-label="Migas de pan" className="text-sm text-gray-700">
          <Link to="/galeria" className="hover:text-brand-orange-deep hover:underline">
            Galerías
          </Link>
          <span aria-hidden="true" className="mx-2">/</span>
          <span aria-current="page">{galeria.titulo}</span>
        </nav>

        <header className="mt-6">
          <p className="text-sm font-semibold uppercase tracking-widest text-brand-orange-deep">
            Galería
          </p>
          <h1 className="mt-2 max-w-3xl font-display text-5xl font-semibold leading-tight md:text-6xl">
            {galeria.titulo}
          </h1>
          <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-gray-700">
            {fechaTxt && <span>{fechaTxt}</span>}
            <span aria-label={`${fotos.length} fotografías`}>
              {fotos.length} {fotos.length === 1 ? "foto" : "fotos"}
            </span>
            {contextoLink && (
              <Link
                to={contextoLink.to}
                className="font-semibold text-brand-orange-deep underline underline-offset-4 hover:text-brand-orange"
              >
                {contextoLink.label}
              </Link>
            )}
          </div>
          {galeria.descripcion && (
            <p className="mt-6 max-w-3xl text-lg text-gray-700">
              {galeria.descripcion}
            </p>
          )}
        </header>

        {fotos.length === 0 ? (
          <p className="mt-12 text-gray-700">
            Aún no se han subido fotografías a esta galería.
          </p>
        ) : (
          <ul className="mt-12 grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">
            {fotos.map((foto, idx) => {
              const thumbSrc =
                mediaUrl(foto.formats?.medium?.url) ??
                mediaUrl(foto.formats?.small?.url) ??
                mediaUrl(foto.url);
              return (
                <li key={foto.documentId ?? foto.id}>
                  <button
                    type="button"
                    onClick={() => setLightboxIndex(idx)}
                    aria-label={
                      foto.alternativeText
                        ? `Abrir imagen: ${foto.alternativeText}`
                        : `Abrir imagen ${idx + 1}`
                    }
                    className="group block aspect-square w-full overflow-hidden rounded-lg bg-brand-paper ring-1 ring-gray-300 transition hover:ring-brand-orange focus:outline-none focus:ring-2 focus:ring-brand-orange focus:ring-offset-2"
                  >
                    <img
                      src={thumbSrc}
                      alt={foto.alternativeText ?? ""}
                      className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                      loading="lazy"
                    />
                  </button>
                </li>
              );
            })}
          </ul>
        )}

        <div className="mt-16">
          <Link
            to="/galeria"
            className="text-sm font-semibold text-brand-ink hover:text-brand-orange-deep hover:underline"
          >
            ← Volver a todas las galerías
          </Link>
        </div>
      </article>

      {lightboxIndex !== null && fotos.length > 0 && (
        <Lightbox
          fotos={fotos}
          index={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
          onIndexChange={setLightboxIndex}
        />
      )}
    </>
  );
}
